import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { callErrorToast } from "../../../utilities/index.js";
import ParcelTemplatesService from "../../../services/shipment-service/parcelTemplates.ts";

const parcelTemplatesService = new ParcelTemplatesService();

interface DeleteParcelTemplateProviderProps {
  children: ReactNode;
  data?: string[];
  callback?: () => void;
}

interface DeleteParcelTemplateContextValue {
  loader: boolean;
  deleteParcelTemplates: () => void;
  data: string[];
  callback?: () => void;
}

const DeleteParcelTemplateContext = createContext<
  DeleteParcelTemplateContextValue | undefined
>(undefined);

const DeleteParcelTemplateProvider: React.FC<
  DeleteParcelTemplateProviderProps
> = ({ children, data = [], callback }) => {
  const [error, setError] = useState<unknown>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const deleteParcelTemplates = (): void => {
    setLoader(true);
    const promises = data.map((id) => parcelTemplatesService.delete(id));
    Promise.all(promises)
      .then(() => {
        setLoader(false);
        if (callback) {
          callback();
        }
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  return (
    <DeleteParcelTemplateContext.Provider
      value={{ loader, deleteParcelTemplates, data, callback }}
    >
      {children}
    </DeleteParcelTemplateContext.Provider>
  );
};

const useDeleteParcelTemplateContext = (): DeleteParcelTemplateContextValue => {
  const context = useContext(DeleteParcelTemplateContext);
  if (!context) {
    throw new Error(
      "useDeleteParcelTemplateContext must be used within a DeleteParcelTemplateProvider"
    );
  }
  return context;
};

export { DeleteParcelTemplateProvider, useDeleteParcelTemplateContext };
